import { useI18n } from '@pique/i18n';
import clsx from 'clsx';
import { AccountSetting03Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { type MouseEvent, useCallback } from 'react';

import { AdminBadge } from '../../../../hooks/use-is-admin-or-staff';
import { useGeneralSettingList } from '../general-setting';
import type { ActiveTab } from '../types';
import * as style from './style.css';

export const SettingSidebar = ({
  activeTab,
  onTabChange,
}: {
  activeTab: ActiveTab;
  onTabChange: (key: ActiveTab) => void;
}) => {
  const t = useI18n();
  const generalList = useGeneralSettingList();

  const gotoTab = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const tab = e.currentTarget.dataset['eventArg'];
      if (!tab) return;
      onTabChange(tab as ActiveTab);
    },
    [onTabChange]
  );

  return (
    <div className={style.settingSlideBar} data-testid="settings-sidebar">
      <div className={style.sidebarTitle}>
        {t['com.pique.settingSidebar.title']()}
      </div>
      <div className={style.sidebarSubtitle}>Account</div>
      <div className={style.sidebarItemsWrapper}>
        <div
          className={clsx(style.sidebarSelectItem, {
            active: activeTab === 'account',
          })}
          title="Account"
          data-event-arg="account"
          onClick={gotoTab}
          data-testid="account-panel-trigger"
        >
          <HugeiconsIcon icon={AccountSetting03Icon} className="icon" />
          <span className="setting-name">Account</span>
        </div>
      </div>
      <div className={style.sidebarSubtitle}>
        {t['com.pique.settingSidebar.settings.general']()}
      </div>
      <div className={style.sidebarItemsWrapper}>
        {generalList.map(
          ({ title, icon, key, testId, isAdminOnly, isFeatureFlagged }) => (
            <div
              className={clsx(style.sidebarSelectItem, {
                active: key === activeTab,
                featureFlagged: isFeatureFlagged,
              })}
              key={key}
              title={title}
              data-event-arg={key}
              onClick={gotoTab}
              data-testid={testId}
            >
              {icon({ className: 'icon' })}
              <span className="setting-name">{title}</span>
              {isAdminOnly && <AdminBadge />}
            </div>
          )
        )}
      </div>
    </div>
  );
};
